import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { Status } from '../../models/common/Status';
import { SendReportPolicy } from '../../models/enums/SendreportPolicy';
import { Campaign } from '../../models/RoboCall/Campaign';
import { CampaignInfo } from '../../models/RoboCall/CampaignsInfo';
import { ClientPhoneData } from '../../models/RoboCall/ClientPhoneData';
import { RootState } from '../store';
import { apiClientWithApi } from '../../utils/http';

export interface CampaignState {
  campaigns: Campaign[];
  campaignsInfo: CampaignInfo[];
  campaignRequest: CampaignRequest | null;
  totalCount: number;
  status: Status;
}

export interface CampaignRequest {
  name: string;
  orgName: string;
  callDateTime: Dayjs[];
  startCallDateTime: string;
  endCallDateTime: string;
  maxTries: number;
  retryInterval: number;
  maxThreads: number;
  phone: string;
  maxSuccessfulCalls: number;
  dialAccountId: number;
  operatorAccountId: number;
  sendReportPolicy: SendReportPolicy;
  askIfReadyToTalk: boolean;
  askToConnect: boolean;
  identifyByName: boolean;
  selectedQuestionnaire: number;
  clientPhones: ClientPhoneData[];
}

export interface GetCampaignListResponse {
  id: number;
  name: string;
}

export interface GetCampaignResponse {
  response: Campaign[];
  totalCount: number;
}

export interface GetCampaignRequest {
  pageNumber: number;
  pageSize: number;
}

export interface TurnCampaignRequest {
  id: number;
  turn: boolean;
}

const initialState: CampaignState = {
  campaigns: [],
  campaignsInfo: [],
  totalCount: 0,
  campaignRequest: null,
  status: {
    responseLoading: true,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

export const getCampaignCopy = createAsyncThunk(
  'campaign/getCampaignCopy',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<CampaignRequest>(
        'campaign/campaign-copy',
        { params: { id: id } },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else return rejectWithValue('Не удалось скопировать кампанию!');
    }
  },
);

export const getCampaignsList = createAsyncThunk(
  'сampaign/getCampaignList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<CampaignInfo[]>(
        'campaign/campaign-list',
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else return rejectWithValue('Не удалось получить список кампаний!');
    }
  },
);

export const getCampaigns = createAsyncThunk(
  'сampaign/getCampaigns',
  async (request: GetCampaignRequest, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<GetCampaignResponse>(
        'campaign',
        {
          params: {
            pageNumber: request.pageNumber,
            pageSize: request.pageSize,
          },
        },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else return rejectWithValue('Не удалось получить список кампаний!');
    }
  },
);

export const createCampaign = createAsyncThunk(
  'сampaign/createCampaign',
  async (campaign: CampaignRequest, { rejectWithValue }) => {
    try {
      await apiClientWithApi.post<number>('campaign', campaign);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else return rejectWithValue('Не удалось создать кампанию!');
    }
  },
);

export const turnCampaign = createAsyncThunk(
  'сampaign/turnCampaign',
  async (request: TurnCampaignRequest, { rejectWithValue }) => {
    try {
      await apiClientWithApi.post<number>('campaign/turn-campaign', undefined, {
        params: { id: request.id, turn: request.turn },
      });
      return request;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else return rejectWithValue('Не удалось создать кампанию!');
    }
  },
);

export const updloadTasks = createAsyncThunk(
  'campaign/updloadTasks',
  async (request: FormData, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.post<ClientPhoneData[]>(
        'campaign/upload-tasks',
        request,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else return rejectWithValue('Не удалось получить список заданий!');
    }
  },
);

export const campaignSlice = createSlice({
  name: 'сampaign',
  initialState,
  reducers: {
    updateRequestStatus: (state) => {
      state.status.requestSuccess = null;
      state.status.requestError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getCampaigns.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(getCampaigns.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.campaigns = action.payload.response;
        state.totalCount = action.payload.totalCount;
        return state;
      })
      .addCase(getCampaignCopy.fulfilled, (state, action) => {
        state.campaignRequest = {
          ...action.payload,
          callDateTime: [
            dayjs(action.payload.startCallDateTime, 'DD.MM.YYYY'),
            dayjs(action.payload.endCallDateTime, 'DD.MM.YYYY'),
          ],
        };
        return state;
      })
      .addCase(getCampaignsList.pending, (state) => {
        state.status.responseLoading = true;
        return state;
      })
      .addCase(getCampaignsList.fulfilled, (state, action) => {
        state.campaignsInfo = action.payload;
        state.status.responseLoading = false;
        return state;
      })
      .addCase(turnCampaign.fulfilled, (state, action) => {
        let index = state.campaigns.findIndex(
          (x) => x.id === action.payload.id,
        );

        state.campaigns[index].turnedOn = action.payload.turn;
      })
      .addCase(getCampaigns.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить кампании';
        return state;
      })
      .addCase(createCampaign.fulfilled, (state) => {
        state.status.requestSuccess = 'Кампания успешно создана';
        state.status.requestError = null;
      })
      .addCase(createCampaign.rejected, (state) => {
        state.status.requestSuccess = null;
        state.status.requestError = 'Ошибка при создании кампании';
      });
  },
});

export default campaignSlice.reducer;

export const selectCampaigns = (state: RootState) => state.campaign.campaigns;

export const selectCampaignRequest = (state: RootState) =>
  state.campaign.campaignRequest;

export const { updateRequestStatus } = campaignSlice.actions;

export const campaignsResponseLoading = (state: RootState) =>
  state.campaign.status.responseLoading;

export const campaignsCount = (state: RootState) => state.campaign.totalCount;

export const campaignsInfo = (state: RootState) => state.campaign.campaignsInfo;

export const campaignsStatus = (state: RootState) => state.campaign.status;
